import { API_BASE } from '@/app.config'
import type {
  ApiErrorBody,
  ApiErrorCode,
  ChangePasswordRequest,
  CreateUserRequest,
  ImportMembersResponse,
  LoginResponse,
  Member,
  PullResponse,
  PushResponse,
  Reason,
  SyncOp,
  UpdateMemberRequest,
  UpdateUserRequest,
  UserPublic,
} from '@cadoan/shared'

/** The console's only network module. Same contract as the phone's, plus /admin. */

const TIMEOUT_MS = 20_000

export type FailureKind = 'network' | 'api' | 'server'

export class ApiFailure extends Error {
  constructor(
    readonly kind: FailureKind,
    readonly status: number,
    readonly code: ApiErrorCode | null,
    readonly extra: Omit<ApiErrorBody['error'], 'code'> = {},
  ) {
    super(code ?? kind)
  }
}

let token: string | null = null
let onAuthLost: ((code: ApiErrorCode) => void) | null = null

export function setToken(next: string | null): void {
  token = next
}

/** Called once per lost session (expired, locked, demoted) so the app can sign out. */
export function onAuthLostHandler(fn: (code: ApiErrorCode) => void): void {
  onAuthLost = fn
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    })
  } catch {
    throw new ApiFailure('network', 0, null)
  } finally {
    clearTimeout(timer)
  }
  if (res.status === 204) return undefined as T
  let data: unknown = null
  try {
    data = await res.json()
  } catch {
    throw new ApiFailure('server', res.status, null)
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error
    if (!err?.code || res.status >= 500)
      throw new ApiFailure('server', res.status, err?.code ?? null)
    const { code, ...extra } = err
    if (path !== '/auth/login' && (res.status === 401 || code === 'forbidden')) onAuthLost?.(code)
    throw new ApiFailure('api', res.status, code, extra)
  }
  return data as T
}

export const api = {
  login: (username: string, password: string) =>
    request<LoginResponse>('POST', '/auth/login', { username, password, app: 'console' }),
  me: () => request<UserPublic>('GET', '/auth/me'),
  logout: () => request<void>('POST', '/auth/logout'),
  changePassword: (body: ChangePasswordRequest) =>
    request<void>('POST', '/auth/change-password', body),

  pull: (since: number) => request<PullResponse>('GET', `/sync?since=${since}`),
  push: (ops: SyncOp[]) => request<PushResponse>('POST', '/sync/push', { ops }),

  listUsers: () => request<UserPublic[]>('GET', '/admin/users'),
  createUser: (body: CreateUserRequest) => request<UserPublic>('POST', '/admin/users', body),
  updateUser: (id: string, body: UpdateUserRequest) =>
    request<UserPublic>('PATCH', `/admin/users/${id}`, body),
  resetPassword: (id: string, password: string) =>
    request<UserPublic>('POST', `/admin/users/${id}/reset-password`, { password }),

  createMember: (name: string, joinedOn: string) =>
    request<Member>('POST', '/members', { name, joinedOn }),
  updateMember: (id: string, body: UpdateMemberRequest) =>
    request<Member>('PATCH', `/members/${id}`, body),
  reorderMembers: (ids: string[]) => request<Member[]>('POST', '/admin/members/reorder', { ids }),
  importMembers: (names: string[], joinedOn: string) =>
    request<ImportMembersResponse>('POST', '/admin/members/import', { names, joinedOn }),

  deleteMeeting: (id: string) => request<void>('DELETE', `/admin/meetings/${id}`),

  createReason: (label: string) => request<Reason>('POST', '/admin/reasons', { label }),
  renameReason: (id: string, label: string) =>
    request<Reason>('PATCH', `/admin/reasons/${id}`, { label }),
  deleteReason: (id: string) => request<void>('DELETE', `/admin/reasons/${id}`),
  reorderReasons: (ids: string[]) => request<Reason[]>('POST', '/admin/reasons/reorder', { ids }),
}
